import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { fetcher, swrOpts, unitPrice } from "@/lib/api";

const KEY = "shadc_cart_v1";
const CartCtx = createContext(null);
const SiteCtx = createContext(null);

const read = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || [];
  } catch {
    return [];
  }
};

export function Providers({ children }) {
  const [items, setItems] = useState(read);
  const [open, setOpen] = useState(false);
  const { data: site } = useSWR("/site", fetcher, swrOpts);

  useEffect(() => localStorage.setItem(KEY, JSON.stringify(items)), [items]);
  useEffect(() => {
    const h = (e) => e.key === KEY && setItems(read());
    window.addEventListener("storage", h);
    return () => window.removeEventListener("storage", h);
  }, []);

  const add = useCallback((p, options = {}, qty = 1) => {
    const key = `${p.id}|${JSON.stringify(options)}`;
    setItems((prev) => {
      const ex = prev.find((i) => i.key === key);
      if (ex) return prev.map((i) => (i.key === key ? { ...i, quantity: Math.min(99, i.quantity + qty) } : i));
      return [...prev, { key, product_id: p.id, slug: p.slug, name: p.name, image: p.thumb || p.image, options,
        unit_price: unitPrice(p, options), quantity: qty }];
    });
    toast.success(`${p.name} added to cart`);
    setOpen(true);
  }, []);

  const update = useCallback((key, q) =>
    setItems((prev) => (q <= 0 ? prev.filter((i) => i.key !== key) : prev.map((i) => (i.key === key ? { ...i, quantity: Math.min(99, q) } : i)))), []);
  const clear = useCallback(() => setItems([]), []);

  const cart = useMemo(() => ({
    items, add, update, clear, open, setOpen,
    count: items.reduce((s, i) => s + i.quantity, 0),
    subtotal: items.reduce((s, i) => s + i.unit_price * i.quantity, 0),
  }), [items, add, update, clear, open]);

  return (
    <SiteCtx.Provider value={site}>
      <CartCtx.Provider value={cart}>{children}</CartCtx.Provider>
    </SiteCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
export const useSite = () => useContext(SiteCtx);
